/**
 * Authentication service
 * Supports multiple auth providers (Local, Azure AD, Mock Azure AD)
 */
import { BaseService } from '@services/base.service';
import { LoginRequest, RegisterRequest, AuthResponse } from '../types/auth.types';
import { authConfig, AuthProvider } from '@core/config/auth.config';
import { azureADService } from './azure-ad.service';
import { mockAzureADService } from './mock-azure-ad.service';
import { mockAuthService } from './mock-auth.service';
import { axiosInstance } from '@core/config/axios.config';
import { ApiResponse } from '@core/types';

class AuthService extends BaseService<any> {
    constructor() {
        super('/auth');
    }

    /**
     * Get the appropriate Azure AD service (real or mock)
     */
    private getAzureADService() {
        return authConfig.useMockAzureAD ? mockAzureADService : azureADService;
    }

    /**
     * Check if we should use mock auth
     */
    private shouldUseMock(): boolean {
        return import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_AUTH === 'true';
    }

    /**
     * Initialize authentication
     */
    async initialize(): Promise<void> {
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            await azureService.initialize();

            if (authConfig.useMockAzureAD) {
                console.log('✅ Mock Azure AD initialized (Development mode)');
            } else {
                console.log('✅ Real Azure AD initialized');
            }
        } else if (this.shouldUseMock()) {
            console.log('✅ Mock auth initialized (Development mode)');
        } else {
            console.log('✅ Local auth initialized');
        }
    }

    /**
     * Handle redirect callback from Azure AD
     * Call this on app load to check if user just returned from Azure AD redirect
     */
    async handleRedirectCallback(): Promise<AuthResponse | null> {
        if (authConfig.provider !== AuthProvider.AZURE_AD) {
            return null;
        }

        const azureService = this.getAzureADService();
        const redirectResult = await azureService.handleRedirectPromise();

        if (!redirectResult) {
            return null;
        }

        // User just returned from Azure AD, complete the SSO flow
        try {
            const idToken = redirectResult.idToken;
            if (!idToken) {
                throw new Error('No ID token in redirect response');
            }

            // Send ID token to backend SSO endpoint
            const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
                `${this.endpoint}/sso/azure-ad`,
                { id_token: idToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = response.data as any;
            if (!response?.data) {
                throw new Error('Invalid SSO response structure');
            }

            const authData: AuthResponse = {
                user: {
                    ...data.user,
                    name: data?.user?.first_name + ' ' + data?.user?.last_name,
                    roles: [data?.user?.role]
                },
                accessToken: data.accessToken || data.access_token,
                refreshToken: data.refreshToken || data.refresh_token || '',
            };

            console.log('✅ SSO login completed via redirect');
            return authData;
        } catch (error) {
            console.error('❌ SSO redirect callback failed:', error);
            throw error;
        }
    }

    /**
     * Login - supports Azure AD (real/mock), Mock, and Real API
     */
    async login(credentials: LoginRequest): Promise<AuthResponse> {
        // Azure AD Login (real or mock) - uses SSO endpoint
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            return this.loginWithAzureADSSO();
        }

        // Mock Login (Development)
        if (this.shouldUseMock()) {
            return mockAuthService.login(credentials);
        }

        // Real API Login
        const response = await axiosInstance.post(`${this.endpoint}/login`, credentials);
        return response.data.data;
    }

    /**
     * Azure AD SSO Login
     * 1. Authenticate with Azure AD (MSAL)
     * 2. Get ID token from Azure AD
     * 3. Send ID token to backend SSO endpoint
     * 4. Backend validates and returns app tokens
     */
    async loginWithAzureADSSO(): Promise<AuthResponse> {
        const azureService = this.getAzureADService();

        try {
            // Step 1: Authenticate with Azure AD
            await azureService.loginWithPopup();
        } catch (error: any) {
            // If user cancelled, re-throw the cancellation error
            if (error?.isUserCancelled === true || error?.message === 'USER_CANCELLED') {
                throw error;
            }
            // For other errors, re-throw as well
            throw error;
        }

        // Step 2: Get ID token from Azure AD
        const idToken = await azureService.getIdTokenAsync();
        if (!idToken) {
            throw new Error('Failed to obtain ID token from Azure AD');
        }

        // Step 3 & 4: Send ID token to backend SSO endpoint
        const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
            `${this.endpoint}/sso/azure-ad`,
            { id_token: idToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                    // 'ngrok-skip-browser-warning': 'true',
                },
            }
        );
        const data = response.data as any;
        if (!response?.data) {
            console.error('❌ Invalid SSO response structure. Full response:', {
                responseData: response.data,
                responseStatus: response.status,
                responseHeaders: response.headers,
            });
            throw new Error('❌ Invalid SSO response structure. Full response:');
        }
        const authData: AuthResponse = {
            user: { ...data.user, name: data?.user?.first_name + ' ' + data?.user.last_name, roles: [data?.user?.role] },
            accessToken: data.accessToken || data.access_token,
            refreshToken: data.refreshToken || data.refresh_token || '',
        };
        return authData;
    }


    /**
     * Register - only for local/mock auth
     */
    async register(data: RegisterRequest): Promise<AuthResponse> {
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            throw new Error('Registration not supported with Azure AD');
        }

        // Mock Register (Development)
        if (this.shouldUseMock()) {
            return mockAuthService.register(data);
        }

        // Real API Register
        const response = await axiosInstance.post(`${this.endpoint}/register`, data);
        return response.data.data;
    }

    /**
     * Logout - clears local session only, no API call needed
     */
    async logout(): Promise<void> {
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            await azureService.logout();
            return;
        }

        if (this.shouldUseMock()) {
            await mockAuthService.logout();
            return;
        }

        // For real API, no logout endpoint call needed
        // Just clear local tokens (handled in useAuth hook)
        return Promise.resolve();
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            return azureService.isAuthenticated();
        }
        return false;
    }

    /**
     * Dev Login - Generate token via API key (LOCAL/DEV environment only)
     * This bypasses Azure AD for easier testing
     */
    async devLogin(email: string, apiKey: string): Promise<AuthResponse> {
        if (!apiKey) {
            throw new Error('API key is required');
        }

        const response = await axiosInstance.post(
            `${this.endpoint}/test/token`,
            null,
            {
                params: { email },
                headers: {
                    'X-API-Key': apiKey,
                },
            }
        );

        const data = response.data as any;
        if (!data) {
            throw new Error('Invalid dev login response');
        }

        const authData: AuthResponse = {
            user: {
                ...data.user,
                name: `${data.user?.first_name || ''} ${data.user?.last_name || ''}`.trim(),
                roles: [data.user?.role],
            },
            accessToken: data.accessToken || data.access_token,
            refreshToken: data.refreshToken || data.refresh_token || '',
        };

        console.log('✅ Dev login successful');
        return authData;
    }

    /**
     * Get current user
     */
    async getCurrentUser(): Promise<any> {
        if (authConfig.provider === AuthProvider.AZURE_AD) {
            const azureService = this.getAzureADService();
            return azureService.getUserInfo();
        }

        if (this.shouldUseMock()) {
            return mockAuthService.getCurrentUser();
        }

        const response = await axiosInstance.get(`${this.endpoint}/me`);
        return response.data.data;
    }
}

export const authService = new AuthService();
